import { GETTEXT_ALL_FACILITIES, showMessageError } from '@/shared/lib';
import { create } from 'zustand';
import fetchRmaTicketCountService from '../api/services/fetchRmaTicketCount';
import type { RmaLimitConfig, RmaOptionLists, RmaState } from './rma.types';

export const defaultRmaState = {
  rmaOptionLists: { reasons: {}, patientImpacts: [] } as RmaOptionLists,
  rmaLimitConfig: {} as Partial<RmaLimitConfig>,
  patientReturnConfig: {} as any,
  rmaTicketCount: 0,
  // initialState.goto.rma.request
  requestFilter: { searchValue: undefined, status: 'All statuses' } as any,
  // goto.waitingToReview không có trong initialState — reducer cũ tạo nhánh này khi
  // dispatch đầu tiên, nên default là {}.
  waitingToReturnFilter: {} as any,
};

// Thay cho takeLatest của saga cũ.
let requestId = 0;

/**
 * Zustand store holding RMA state: the RMA option lists, the RMA limit config, patient return configuration, ticket count and the request and waiting-to-return list filters. Alongside plain setters and merge-style filter updaters it exposes fetchRmaTicketCount, which counts clinic-shipment-reminder tickets for a facility (omitting the id for the all-facilities sentinel), ignores superseded responses via a monotonic requestId, and reports failures through showMessageError.
 *
 * @returns The RMA store hook.
 */
const useRmaStore = create<RmaState>()((set) => ({
  ...defaultRmaState,
  setRmaOptionLists: (rmaOptionLists) => set({ rmaOptionLists }),
  setRmaLimitConfig: (rmaLimitConfig) => set({ rmaLimitConfig }),
  setPatientReturnConfig: (patientReturnConfig) => set({ patientReturnConfig }),
  setRmaTicketCount: (rmaTicketCount) => set({ rmaTicketCount }),
  fetchRmaTicketCount: async (facilityId) => {
    requestId += 1;
    const current = requestId;
    try {
      const rmaTicketCount = await fetchRmaTicketCountService({
        clinicShipmentReminder: true,
        facilityId: facilityId === GETTEXT_ALL_FACILITIES ? undefined : facilityId,
      });
      if (current !== requestId) {
        return;
      }
      set({ rmaTicketCount: rmaTicketCount ?? 0 });
    } catch (error) {
      console.error('Failed to fetch RMA ticket count: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
    }
  },
  updateRequestFilter: (payload) =>
    set((state) => ({ requestFilter: { ...state.requestFilter, ...payload } })),
  updateWaitingToReturnFilter: (payload) =>
    set((state) => ({ waitingToReturnFilter: { ...state.waitingToReturnFilter, ...payload } })),
}));

export default useRmaStore;
